import { useRef, useState } from 'react';
import type { AccordionTab } from './Accordion';

type UseAccordionParams = Readonly<{
  activeTabs?: number[];
  isMulti: boolean;
  onTabToggle: (id: number, currentTabs: number[]) => void;
  tabs: AccordionTab[];
}>;

export type AccordionRenderTab = Readonly<{
  headerId: string;
  isOpen: boolean;
  onToggle: () => void;
  panelId: string;
  tab: AccordionTab;
}>;

export type UseAccordionReturn = Readonly<{
  renderTabs: AccordionRenderTab[];
}>;

// Contador de módulo para ids únicos por instancia. React 17 no tiene `useId`,
// así que usamos un contador incremental (patrón `lodash.uniqueId`): determinista,
// único por instancia y sin PRNG (evita el hotspot de seguridad de `Math.random`).
let accordionInstanceCount = 0;

/**
 * Genera un id único y estable para toda la vida de la instancia, calculado una
 * sola vez y conservado en un `useRef`.
 */
function useInstanceId(): string {
  const idRef = useRef<string>();
  if (!idRef.current) {
    accordionInstanceCount += 1;
    idRef.current = `ui-accordion-${accordionInstanceCount}`;
  }
  return idRef.current;
}

function useAccordion({ activeTabs, isMulti, onTabToggle, tabs }: UseAccordionParams): UseAccordionReturn {
  const instanceId = useInstanceId();
  // Estado no controlado: `activeTabs` solo actúa como valor inicial (paridad
  // con el Accordion legacy). El padre se entera de cada cambio vía `onTabToggle`.
  const [openTabs, setOpenTabs] = useState<number[]>(activeTabs ?? []);

  function toggle(id: number): void {
    let currentTabs = [...openTabs];
    if (isMulti) {
      currentTabs = currentTabs.includes(id) ? currentTabs.filter((item) => item !== id) : [...currentTabs, id];
    } else {
      currentTabs = currentTabs[0] === id ? [] : [id];
    }
    setOpenTabs(currentTabs);
    onTabToggle(id, currentTabs);
  }

  const renderTabs: AccordionRenderTab[] = tabs.map((tab, index) => {
    function onToggle(): void {
      toggle(index);
    }

    return {
      headerId: `${instanceId}-tab-${index}-header`,
      isOpen: openTabs.includes(index),
      onToggle,
      panelId: `${instanceId}-tab-${index}-panel`,
      tab,
    };
  });

  return { renderTabs };
}

export default useAccordion;
